import React, { forwardRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff } from 'lucide-react';
import { Input } from './ui/input';

/**
 * PasswordField — password `<Input>` with a Show/Hide eye toggle.
 *
 * Frontend UX only: the toggle just flips the underlying input between
 * `type="password"` and `type="text"`. Authentication, hashing, and
 * validation are unchanged.
 *
 * Accessibility:
 *   • The eye control is a real <button type="button"> so it never
 *     submits the surrounding form.
 *   • `aria-label` reflects the current state and updates on toggle.
 *   • Keyboard focus visible via `focus-visible:ring-*`.
 *   • Autocomplete + password-manager behaviour is preserved because
 *     the same <Input> node is reused (React does not remount on type
 *     change).
 *
 * Props: everything <Input> accepts, plus:
 *   • `showLabel`  — i18n key for the aria-label in "hidden" state.
 *                    Defaults to `auth.showPassword`.
 *   • `hideLabel`  — i18n key for the aria-label in "visible" state.
 *                    Defaults to `auth.hidePassword`.
 *   • `testIdToggle` — data-testid for the toggle button.
 */
const PasswordField = forwardRef(function PasswordField(
  {
    className = '',
    showLabel = 'auth.showPassword',
    hideLabel = 'auth.hidePassword',
    testIdToggle,
    ...inputProps
  },
  ref,
) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  const label = visible ? t(hideLabel) : t(showLabel);

  return (
    <div className={`relative ${className}`}>
      <Input
        ref={ref}
        {...inputProps}
        type={visible ? 'text' : 'password'}
        className={`pr-11 ${inputProps.className || ''}`}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        onMouseDown={(e) => e.preventDefault()}
        aria-label={label}
        aria-pressed={visible}
        title={label}
        data-testid={testIdToggle}
        tabIndex={0}
        className="absolute inset-y-0 right-0 flex items-center justify-center w-11 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#45C04A]/60 rounded-r-2xl"
      >
        {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
});

export default PasswordField;
export { PasswordField };
